// Panel izquierdo de «Cobros»: buscador, chips de filtro y la lista de créditos.
import { Search, X } from 'lucide-react';
import { LoanCreditStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanCreditStatusEnum';
import { ErrorState, Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { NoSource } from '@/shared/patterns/NoSource';
import {
    COLLECTION_FILTER_LABEL,
    COLLECTION_FILTERS,
    statusTagOf,
    owedCents,
    type CollectionCredit,
    type CollectionFilter,
    type ListFacts,
    type StatusTone,
} from '../lib/collectionsModel';
import {
    CREDIT_ERROR_NOTE,
    CREDIT_FORBIDDEN_NOTE,
    dueDateLabel,
    EMPTY_FILTER_TEXT,
    HIGH_END_TITLE,
    money,
    SEARCH_PLACEHOLDER,
    SEARCH_PLACEHOLDER_MINE,
    WALK_IN_BANNER_TEXT,
    WALK_IN_BANNER_TITLE,
} from '../lib/collectionsFormat';

export interface ListEntry {
    row: CollectionCredit;
    facts: ListFacts | null;
}

const STATUS_CLASS: Record<StatusTone, string> = {
    ok: 'bg-success-soft text-success',
    soon: 'bg-warning-soft text-warning',
    arrears: 'bg-danger-soft text-danger',
    settled: 'bg-muted text-muted-foreground',
};

export interface CollectionsListPanelProps {
    query: string;
    onQueryChange: (query: string) => void;
    filter: CollectionFilter;
    onFilterChange: (filter: CollectionFilter) => void;
    counts: Record<CollectionFilter, number>;
    entries: ListEntry[];
    selectedId: string | null;
    onSelect: (creditId: string) => void;
    isWalkIn: boolean;
    /** El usuario puede buscar fuera de su cartera (`loan.credit.collection.write`). Sin él, no se ofrece. */
    canWalkIn: boolean;
    /** Aviso del walk-in que no se pudo correr (sin permiso o caído). */
    walkInNote: string | null;
    isLoading: boolean;
    error: Error | null;
    /** Motivo que reintentar no arregla (sin alta de vendedor); se pinta sin «Reintentar». */
    blockedNote: string | null;
    onRetry: () => void;
}

export function CollectionsListPanel(props: CollectionsListPanelProps) {
    const { query, onQueryChange, filter, onFilterChange, counts } = props;
    return (
        <div className="flex min-h-0 flex-1 flex-col gap-2.5">
            <div className="flex flex-col gap-2 rounded-lg border border-border bg-card px-3 py-2.5">
                <div className="relative">
                    <Search
                        aria-hidden
                        className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
                    />
                    <input
                        type="search"
                        aria-label="Buscar créditos"
                        placeholder={props.canWalkIn ? SEARCH_PLACEHOLDER : SEARCH_PLACEHOLDER_MINE}
                        autoComplete="off"
                        value={query}
                        onChange={(event) => {
                            onQueryChange(event.target.value);
                        }}
                        className="w-full rounded-md border border-border bg-card py-2 pl-8 pr-8 text-[13px] focus:border-primary focus:outline-none [&::-webkit-search-cancel-button]:hidden"
                    />
                    {query ? (
                        <button
                            type="button"
                            title="Limpiar"
                            aria-label="Limpiar"
                            onClick={() => {
                                onQueryChange('');
                            }}
                            className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center rounded text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                            <X className="size-3.5" aria-hidden />
                        </button>
                    ) : null}
                </div>
                <div className="flex flex-wrap gap-1" role="group" aria-label="Filtros">
                    {COLLECTION_FILTERS.map((key) => {
                        const active = filter === key;
                        return (
                            <button
                                key={key}
                                type="button"
                                aria-pressed={active}
                                onClick={() => {
                                    onFilterChange(key);
                                }}
                                className={cn(
                                    'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[10.5px] font-semibold',
                                    active
                                        ? 'border-primary bg-accent text-primary'
                                        : 'border-border bg-card text-muted-foreground hover:border-primary/40',
                                )}
                            >
                                {COLLECTION_FILTER_LABEL[key]}
                                <span
                                    className={cn(
                                        'rounded-lg px-1.5 py-px text-[9.5px] font-bold',
                                        active ? 'bg-primary/20' : 'bg-muted',
                                    )}
                                >
                                    {counts[key]}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-border bg-card">
                <ListBody {...props} />
            </div>
        </div>
    );
}

function ListBody({
    entries,
    query,
    selectedId,
    onSelect,
    isWalkIn,
    canWalkIn,
    walkInNote,
    isLoading,
    error,
    blockedNote,
    onRetry,
}: CollectionsListPanelProps) {
    if (blockedNote) {
        return (
            <div className="px-5 py-10 text-center text-[12.5px] text-muted-foreground">
                {blockedNote}
            </div>
        );
    }
    if (error) return <ErrorState className="m-3" onRetry={onRetry} />;
    if (isLoading && entries.length === 0) {
        return (
            <div className="p-3">
                <Skeleton lines={6} />
            </div>
        );
    }
    if (entries.length === 0) {
        const term = query.trim();
        return (
            <div className="px-5 py-10 text-center text-[12.5px] text-muted-foreground">
                {term ? (
                    <>
                        Sin resultados para &quot;<strong>{term}</strong>&quot;
                        {canWalkIn && term.length < 6 ? (
                            <div className="mt-2 text-[11px]">
                                Escribe el <strong>identificador completo</strong> (número de
                                crédito, IMEI o teléfono de 10 dígitos) para buscar créditos fuera
                                de tu cartera.
                            </div>
                        ) : null}
                        {walkInNote ? <div className="mt-2 text-[11px]">{walkInNote}</div> : null}
                    </>
                ) : (
                    EMPTY_FILTER_TEXT
                )}
            </div>
        );
    }
    return (
        <>
            {isWalkIn ? (
                <div className="border-b border-info/30 bg-info-soft px-3 py-2 text-[11px] text-info">
                    <Search className="mr-1 inline size-3 align-[-2px]" aria-hidden />
                    <strong>{WALK_IN_BANNER_TITLE}</strong>
                    {WALK_IN_BANNER_TEXT}
                </div>
            ) : null}
            <ul aria-label="Créditos" className="divide-y divide-border/50">
                {entries.map((entry) => (
                    <li key={entry.row.creditId}>
                        <CreditItem
                            entry={entry}
                            selected={entry.row.creditId === selectedId}
                            onSelect={onSelect}
                        />
                    </li>
                ))}
            </ul>
        </>
    );
}

function CreditItem({
    entry,
    selected,
    onSelect,
}: {
    entry: ListEntry;
    selected: boolean;
    onSelect: (creditId: string) => void;
}) {
    const { row, facts } = entry;
    const walkInAmount =
        row.read.state === 'walk-in'
            ? (row.read.lookup.nextInstallment?.amountCents ?? null)
            : null;
    return (
        <button
            type="button"
            aria-current={selected ? 'true' : undefined}
            onClick={() => {
                onSelect(row.creditId);
            }}
            className={cn(
                'grid w-full grid-cols-[1fr_auto] items-center gap-2 px-3 py-2.5 text-left transition-colors',
                selected
                    ? 'bg-accent shadow-[inset_3px_0_0_var(--color-primary)]'
                    : 'hover:bg-accent/40',
            )}
        >
            <div className="min-w-0">
                <div className="truncate text-[12.5px] font-bold leading-tight">
                    {row.customerName}
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 text-[10.5px] leading-snug text-muted-foreground">
                    <span className="truncate">
                        {/* eslint-disable-next-line no-restricted-syntax -- el id del crédito es su folio: el mock lo pinta y el walk-in lo busca */}
                        <span className="font-mono">{row.creditId}</span>
                        {row.product ? ` · ${row.product}` : ''}
                        {row.isMine ? (
                            <>
                                {' · '}
                                <span className="font-bold text-primary">MÍO</span>
                            </>
                        ) : null}
                    </span>
                </div>
                <div className="mt-0.5 text-[10.5px] leading-snug text-muted-foreground">
                    <CouponLine entry={entry} />
                </div>
                {facts?.isHighEnd ? (
                    <div className="mt-1 flex flex-wrap gap-1">
                        <span
                            title={HIGH_END_TITLE}
                            className="inline-flex items-center gap-1 rounded-sm bg-info-soft px-1.5 py-px text-[9.5px] font-bold text-info"
                        >
                            Gama alta
                        </span>
                    </div>
                ) : null}
            </div>
            <div className="flex-none text-right">
                {facts?.coupon || walkInAmount !== null ? (
                    <div className="text-[13px] font-extrabold tabular-nums text-primary">
                        {money(facts?.coupon ? owedCents(facts.coupon) : (walkInAmount ?? 0))}
                    </div>
                ) : null}
                {facts ? <StatusTag facts={facts} /> : null}
            </div>
        </button>
    );
}

function CouponLine({ entry }: { entry: ListEntry }) {
    const { row, facts } = entry;
    if (row.summary?.summaryUnavailable) return <NoSource reason={CREDIT_ERROR_NOTE} />;
    if (row.read.state === 'walk-in') {
        const next = row.read.lookup.nextInstallment;
        return next ? (
            <>
                Cupón {next.installmentNumber} · {dueDateLabel(next.dueDate ?? undefined)}
            </>
        ) : null;
    }
    if (!row.summary) {
        if (row.read.state === 'loading') return <Skeleton className="h-3 w-32" />;
        if (row.read.state === 'forbidden') return <NoSource reason={CREDIT_FORBIDDEN_NOTE} />;
        if (row.read.state === 'error') return <NoSource reason={CREDIT_ERROR_NOTE} />;
    }
    if (facts?.coupon) {
        return (
            <>
                Cupón {facts.coupon.installmentNumber}/{facts.termWeeks} ·{' '}
                {dueDateLabel(facts.coupon.dueDate)}
            </>
        );
    }
    return facts?.status === LoanCreditStatusEnum.SETTLED ? <>Todos los cupones pagados</> : null;
}

function StatusTag({ facts }: { facts: ListFacts }) {
    const tag = statusTagOf(facts);
    return (
        <div
            className={cn(
                'mt-0.5 inline-block rounded-sm px-1.5 py-px text-[9px] font-bold uppercase tracking-wide',
                STATUS_CLASS[tag.tone],
            )}
        >
            {tag.label}
        </div>
    );
}
